"use client";

import type React from "react";

import PageError from "@/app/components/PageError";
import PageLoader from "@/app/components/PageLoader";
import { useParietteContext } from "@/app/context";
import { Button } from "@/components/ui/button";
import { getSiteToken, glob, useApi } from "@/lib/pariette";
import { Site } from "@/lib/types";
import { cn } from "@/lib/utils";
import { ChevronLeft } from "lucide-react";
import { useTranslations } from "next-intl";
import Image from "next/image";
import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import { ReactNode, useEffect, useRef, useState } from "react";
import ParietteLogo from "../../../public/aysoft_logo.svg";
import KvkkModal from "../components/KvkkModal";

interface LayoutProps {
  children: ReactNode;
}

const AuthLayout = ({ children }: LayoutProps) => {
  const t = useTranslations("form");
  const g = useTranslations("general");
  const { loading, error, setError } = useParietteContext();
  const [siteData, setSiteData] = useState<Site>();
  const { $get } = useApi(true);
  const path = usePathname();
  const params = useSearchParams();
  const [kvkkData, setKvkkData] = useState<any>(null);
  const [isKvkkOpen, setIsKvkkOpen] = useState<boolean>(false);
  const [banner, setBanner] = useState<any[]>([]);

  const getkvkk = async (u: string) => {
    try {
      const data = await $get(`canvas/${u}`);
      return data.data;
    } catch (error) { }
  };

  const handleKvkkModalClose = () => {
    setIsKvkkOpen(false);
  };

  const handleOpenModal = async (modal: string) => {
    try {
      const data = await getkvkk(modal);
      setKvkkData(data);
      setIsKvkkOpen(true);
    } catch (error) { }
  };

  const fetchData = async () => {
    await $get(`site/${getSiteToken()}`).then((res) => {
      if (res.status) {
        setSiteData(res.data);
        // changeSiteTheme(res.data);
      }
    });
    // .finally(() => {
    //   setLoading(false);
    // });
  };

  const getBanner = async () => {
    await $get("carousel/auth-banners?status=1")
      .then((res) => {
        setBanner(res.data.items);
      })
      .catch((err) => { });
  };

  useEffect(() => {
    getBanner();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setError(glob.errors.noError);
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setError]);

  const titles = {
    Login: g("welcome") + " " + (siteData?.title ?? "Aysoft"),
    "Forgot-password": g("forgotPasswordTitle"),
    "Reset-password": g("resetPasswordTitle"),
    Redirect: g("redirectTitle"),
  };

  const [currentSlide, setCurrentSlide] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isDragging) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % banner.length);
      }, 6000);

      return () => clearInterval(interval);
    }
    return () => { };
  }, [banner.length, isDragging]);

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setStartX(e.clientX);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true);
    setStartX(e.touches[0].clientX);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    e.preventDefault();
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    if (!isDragging) return;

    const endX = e.clientX;
    const diffX = endX - startX;

    if (diffX > 100) {
      // Swiped right
      setCurrentSlide((prev) => (prev - 1 + banner.length) % banner.length);
    } else if (diffX < -100) {
      // Swiped left
      setCurrentSlide((prev) => (prev + 1) % banner.length);
    }

    setIsDragging(false);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!isDragging) return;

    const endX = e.changedTouches[0].clientX;
    const diffX = endX - startX;

    if (diffX > 100) {
      // Swiped right
      setCurrentSlide((prev) => (prev - 1 + banner.length) % banner.length);
    } else if (diffX < -100) {
      // Swiped left
      setCurrentSlide((prev) => (prev + 1) % banner.length);
    }

    setIsDragging(false);
  };

  return (
    <>
      {loading && <PageLoader />}
      {!error.status && <PageError errors={error} />}

      <div className="relative flex min-h-screen flex-col overflow-hidden">
        <div
          className="fixed inset-0 w-full h-full z-[-1]"
          ref={carouselRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {banner.map((item, index) => (
            <div key={index} className={cn("absolute inset-0 transition-opacity duration-1000 ease-in-out", currentSlide === index ? "opacity-100" : "opacity-0")}>
              <div className="absolute inset-0 bg-gradient-to-r opacity-70 dark:opacity-80"></div>
              <Link href="/" className="">
                <Image src={glob.cdn_url + "lg/" + item.photo || "/placeholder.svg"} alt={item.title} className="min-h-full min-w-full object-cover object-center" width={100000} height={100000} />
              </Link>

            </div>
          ))}
        </div>

        <section className="bg-[var(--pr-bg-primary)] text-[var(--pr-text-primary)] shadow-2xl w-full max-w-lg lg:w-[500px] flex-col rounded-md fixed z-10 left-0 right-0 m-auto lg:m-0 lg:left-auto lg:right-12 overflow-y-auto max-h-dvh overflow-x-hidden top-1/2  transform  -translate-y-1/2">
          <div className="space-y-2 flex justify-center flex-col items-center gap-4 bg-primary/90 px-10 py-8">
            <Image src={siteData ? glob.cdn_url + siteData.logo_dark : ParietteLogo} alt="Pariette" width={140} height={70} priority />

            <p className="text-xl text-primary-foreground text-center font-semibold">{params.get("app2fa") ? t("cofirmFromApp") : titles[path.split("/")[2] as keyof typeof titles] ?? titles.Login}</p>
          </div>
          <div className="px-8 pt-8 pb-8 w-full">
            {children}
            {path.split("/")[2] !== "Forgot-password" && (
              <div className="flex flex-col items-start gap-2 mt-2">
                <Button variant={"link"} className="text-muted-foreground p-0 h-6" onClick={() => handleOpenModal("impressum")}>
                  {t("impressum")}
                </Button>
                <Button variant={"link"} className="text-muted-foreground p-0 h-6" onClick={() => handleOpenModal("agb")}>
                  {t("agb")}
                </Button>
              </div>
            )}
            <div>
              <p className="text-center text-sm mt-2 text-muted-foreground">{`Copyright © ${new Date().getFullYear()}`}</p>
              <p className="text-center text-sm font-medium text-muted-foreground">{siteData?.title}</p>
            </div>
          </div>
        </section>
        {isKvkkOpen ? <KvkkModal data={kvkkData} open={isKvkkOpen} onClose={handleKvkkModalClose} /> : <></>}
      </div>
    </>
  );
};

export default AuthLayout;
